import React, { useCallback, useEffect, useRef, useState } from 'react';

const envOrigin = (import.meta.env.VITE_API_ORIGIN || '').trim().replace(/\/$/, '');
const isPages = typeof window !== 'undefined' && /\.pages\.dev$/i.test(window.location?.hostname || '');
const API_BASE = isPages ? '' : (envOrigin || '');
const api = (path) => (API_BASE ? API_BASE + (path.startsWith('/') ? path : `/${path}`) : path);

const STONKFUN_URL = 'https://www.stonkfun.xyz/';

function formatTime(ms) {
  if (!Number.isFinite(ms)) return '-';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function formatUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(4)}`;
}

function stageLabel(stage) {
  if (stage === 'waiting_pair') return '等待底池开放';
  if (stage === 'partial') return '部分底池已开放';
  if (stage === 'monitoring_tokens') return '监控新开 MEME';
  if (stage === 'pair_open') return '底池已开放';
  return stage || '-';
}

function poolStageLabel(stage) {
  if (stage === 'waiting_pair') return '等待开池';
  if (stage === 'monitoring_tokens') return '已开放 · 盯 meme';
  return stage || '-';
}

function eventTypeLabel(type) {
  if (type === 'pair_open') return '官方开池';
  if (type === 'pair_open_sample') return '开池样例';
  if (type === 'new_token') return '新开 MEME';
  if (type === 'any_pool_new') return '任意底池新开';
  if (type === 'any_pool_probe') return '链路验证';
  if (type === 'test') return '测试';
  return type || '-';
}

export default function WanwuMemePage() {
  const [cloudStatus, setCloudStatus] = useState(null);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [cloudMessage, setCloudMessage] = useState('');
  const [cloudBusy, setCloudBusy] = useState('');
  const [minVolume24hUsd, setMinVolume24hUsd] = useState(0);
  const [settingsReady, setSettingsReady] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);

  const settingsSaveTimerRef = useRef(null);
  const settingsHydratedRef = useRef(false);
  const applyingRemoteRef = useRef(false);
  const settingsRef = useRef({ minVolume24hUsd });
  const loadCloudStatusRef = useRef(null);

  useEffect(() => {
    settingsRef.current = { minVolume24hUsd };
  }, [minVolume24hUsd]);

  const applySettingsFromCloud = useCallback((data) => {
    if (!data || typeof data !== 'object') return;
    applyingRemoteRef.current = true;
    if (data.minVolume24hUsd != null && Number.isFinite(Number(data.minVolume24hUsd))) {
      setMinVolume24hUsd(Math.max(0, Number(data.minVolume24hUsd)));
    }
    window.setTimeout(() => {
      applyingRemoteRef.current = false;
    }, 0);
  }, []);

  const saveSettingsToCloud = useCallback(async (partial = {}, { quiet = false } = {}) => {
    const next = {
      minVolume24hUsd: partial.minVolume24hUsd ?? settingsRef.current.minVolume24hUsd,
      ...partial,
    };
    if (!quiet) setCloudBusy('settings');
    try {
      const resp = await fetch(api('/api/config'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wanwuMeme: next }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `保存失败 ${resp.status}`);
      setSettingsDirty(false);
      if (!quiet) setCloudMessage('配置已保存，云端 Cron / 企微通知将按新参数执行');
      await loadCloudStatusRef.current?.({ skipApplySettings: true, keepMessage: true });
      return true;
    } catch (err) {
      if (!quiet) setCloudMessage(err?.message || '配置保存失败');
      return false;
    } finally {
      if (!quiet) setCloudBusy('');
    }
  }, []);

  const loadCloudStatus = useCallback(async (opts = {}) => {
    setCloudLoading(true);
    try {
      const resp = await fetch(api('/api/wanwu-meme/status'), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `状态读取失败 ${resp.status}`);
      const data = body.data || null;
      setCloudStatus(data);
      if (data && !opts.skipApplySettings) {
        applySettingsFromCloud(data);
        settingsHydratedRef.current = true;
        setSettingsReady(true);
      }
      if (!opts.keepMessage) setCloudMessage('');
    } catch (err) {
      setCloudMessage(err?.message || '云端状态读取失败');
      if (!settingsHydratedRef.current) {
        settingsHydratedRef.current = true;
        setSettingsReady(true);
      }
    } finally {
      setCloudLoading(false);
    }
  }, [applySettingsFromCloud]);

  loadCloudStatusRef.current = loadCloudStatus;

  useEffect(() => {
    loadCloudStatus();
    const timer = window.setInterval(() => loadCloudStatus({ skipApplySettings: true, keepMessage: true }), 30000);
    return () => window.clearInterval(timer);
  }, [loadCloudStatus]);

  useEffect(() => {
    if (!settingsHydratedRef.current || !settingsReady || applyingRemoteRef.current) return undefined;
    setSettingsDirty(true);
    if (settingsSaveTimerRef.current) window.clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = window.setTimeout(() => {
      if (applyingRemoteRef.current) return;
      saveSettingsToCloud({}, { quiet: false });
    }, 800);
    return () => {
      if (settingsSaveTimerRef.current) window.clearTimeout(settingsSaveTimerRef.current);
    };
  }, [minVolume24hUsd, settingsReady, saveSettingsToCloud]);

  const saveCloudEnabled = useCallback(async (enabled) => {
    setCloudBusy('save');
    try {
      const resp = await fetch(api('/api/config'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wanwuMeme: { enabled: !!enabled } }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `保存失败 ${resp.status}`);
      setCloudMessage(enabled ? '云端监控已开启（每分钟 Cron）' : '云端监控已关闭');
      await loadCloudStatus({ skipApplySettings: true, keepMessage: true });
    } catch (err) {
      setCloudMessage(err?.message || '开关保存失败');
    } finally {
      setCloudBusy('');
    }
  }, [loadCloudStatus]);

  const saveAnyPoolTest = useCallback(async (enabled) => {
    setCloudBusy('anyPool');
    try {
      const payload = enabled
        ? { anyPoolTestEnabled: true, anyPoolSeeded: false, anyPoolSeenMints: [] }
        : { anyPoolTestEnabled: false };
      const resp = await fetch(api('/api/config'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wanwuMeme: payload }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `保存失败 ${resp.status}`);
      setCloudMessage(
        enabled
          ? '任意底池测试已开启：首次 Cron 会冷启动建基线，之后新 mint 推企微。验证完请点「关闭任意底池测试」。'
          : '任意底池测试已关闭',
      );
      await loadCloudStatus({ skipApplySettings: true, keepMessage: true });
    } catch (err) {
      setCloudMessage(err?.message || '任意底池测试开关失败');
    } finally {
      setCloudBusy('');
    }
  }, [loadCloudStatus]);

  const runCloudAction = useCallback(async (mode) => {
    const qs = mode === 'dry'
      ? '?dry=1'
      : mode === 'test'
        ? '?test=1'
        : mode === 'anyProbe'
          ? '?anyProbe=1'
          : mode === 'pairProbe'
            ? '?pairProbe=1'
            : '';
    setCloudBusy(mode === 'dry' ? 'dry' : mode === 'test' ? 'test' : mode === 'anyProbe' ? 'anyProbe' : mode === 'pairProbe' ? 'pairProbe' : 'run');
    try {
      const resp = await fetch(api(`/api/cron/wanwu-meme${qs}`), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `请求失败 ${resp.status}`);
      const data = body.data || {};
      setCloudMessage(data.reason || (mode === 'test' ? '测试已完成' : '检查完成'));
      await loadCloudStatus({ skipApplySettings: true, keepMessage: true });
    } catch (err) {
      setCloudMessage(err?.message || '云端动作失败');
    } finally {
      setCloudBusy('');
    }
  }, [loadCloudStatus]);

  const events = Array.isArray(cloudStatus?.recentEvents) ? cloudStatus.recentEvents : [];
  const stageOk = cloudStatus?.stage === 'monitoring_tokens';
  const anyPoolOn = !!cloudStatus?.anyPoolTestEnabled;

  return (
    <div className="volume-spike-page primary-arb-page">
      <section className="cxmt-hero">
        <div>
          <span className="cxmt-eyebrow">StonkFun · Multi Pool</span>
          <h1>万物MEME</h1>
          <p>
            云端每分钟轮询 StonkFun：监控 <strong>LLY / Unitree / MRNA</strong> 三个官方底池。
            任一底池一出现就先企微通知（Stage A，此时可能还没人发 meme），再分别盯该底池新开 MEME（Stage B）。
          </p>
        </div>
        <div className="cxmt-hero-actions">
          <a href={STONKFUN_URL} target="_blank" rel="noreferrer">打开 StonkFun</a>
          <button type="button" disabled={!!cloudBusy || cloudLoading} onClick={() => loadCloudStatus()}>
            刷新状态
          </button>
        </div>
      </section>

      <section className="volume-spike-panel volume-spike-cloud">
        <div className="volume-spike-panel-head">
          <div>
            <h2>链路验证 · 任意底池新发测试</h2>
            <p>
              不限底池：轮询 <code>tokens?sort=newest</code>，真正监控到新 mint 后推企业微信。
              用于确认「轮询 → 识别 → 企微」整条链路；验证通过后请手动关闭，避免刷屏。
            </p>
          </div>
          <span className={`volume-spike-badge tone-${anyPoolOn ? 'bad' : 'muted'}`}>
            {anyPoolOn ? '测试监控中' : '测试已关闭'}
          </span>
        </div>

        <div className="volume-spike-cloud-grid">
          <div>
            <span>测试开关</span>
            <strong>{anyPoolOn ? '已开启' : '已关闭'}</strong>
            <small>与 LLY 正式监控相互独立</small>
          </div>
          <div>
            <span>冷启动</span>
            <strong>{cloudStatus?.anyPoolSeeded ? '已完成' : '待建基线'}</strong>
            <small>已见 {cloudStatus?.anyPoolSeenCount ?? 0} 个 mint</small>
          </div>
          <div>
            <span>最近任意底池检查</span>
            <strong>{cloudStatus?.anyPoolLastCheckAt ? formatTime(cloudStatus.anyPoolLastCheckAt) : '-'}</strong>
            <small>最近报警 {cloudStatus?.anyPoolLastAlertAt ? formatTime(cloudStatus.anyPoolLastAlertAt) : '暂无'}</small>
          </div>
          <div>
            <span>每轮最多推送</span>
            <strong>{cloudStatus?.anyPoolMaxAlertsPerTick ?? 5} 条</strong>
            <small>防止一分钟刷爆企微</small>
          </div>
        </div>

        <div className="volume-spike-cloud-actions">
          <button
            type="button"
            disabled={!!cloudBusy}
            onClick={() => saveAnyPoolTest(!anyPoolOn)}
          >
            {cloudBusy === 'anyPool'
              ? '保存中…'
              : (anyPoolOn ? '关闭任意底池测试' : '开启任意底池测试')}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('anyProbe')}>
            {cloudBusy === 'anyProbe' ? '推送中…' : '立刻用最新一条验证并推企微'}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('run')}>
            {cloudBusy === 'run' ? '检查中…' : '立即轮询一次'}
          </button>
        </div>
      </section>

      <section className="volume-spike-panel volume-spike-cloud">
        <div className="volume-spike-panel-head">
          <div>
            <h2>Stage A · 官方开池通知（LLY / Unitree / MRNA）</h2>
            <p>
              每分钟扫 StonkFun <code>pairs</code> + <code>category=xstock</code>。
              三个底池各自独立：一出现立刻推企微——<strong>不必等有人部署 meme</strong>；各自只推一次开池通知。
            </p>
          </div>
          <span className={`volume-spike-badge tone-${cloudStatus?.stage === 'monitoring_tokens' ? 'ok' : 'warm'}`}>
            {stageLabel(cloudStatus?.stage)}
          </span>
        </div>
        <div className="volume-spike-cloud-grid">
          {(cloudStatus?.pools?.length
            ? cloudStatus.pools
            : [
                { id: 'lly', label: 'LLY', symbols: ['LLYX', 'LLY'] },
                { id: 'unitree', label: 'Unitree', symbols: ['UNITREE'] },
                { id: 'mrna', label: 'MRNA', symbols: ['MRNA'] },
              ]
          ).map((p) => (
            <div key={p.id}>
              <span>{p.label}</span>
              <strong>{poolStageLabel(p.stage)}</strong>
              <small>
                {p.quoteMint
                  ? `${p.quoteSymbol || p.label} · ${String(p.quoteMint).slice(0, 10)}…${p.pairOpenAlerted ? ' · 已通知' : ''}`
                  : `等待 ${(p.symbols || []).join('/') || p.label} 出现`}
              </small>
            </div>
          ))}
          <div>
            <span>企业微信 / Cron</span>
            <strong>{cloudStatus?.hasWeCom ? '已配置' : '未配置'}</strong>
            <small>{cloudStatus?.cron || '*/1 * * * *'} · 最近检查 {cloudStatus?.lastPairCheckAt ? formatTime(cloudStatus.lastPairCheckAt) : '-'}</small>
          </div>
        </div>
        <div className="volume-spike-cloud-actions">
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('pairProbe')}>
            {cloudBusy === 'pairProbe' ? '推送中…' : '推送开池样例/已发现底池'}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('run')}>
            {cloudBusy === 'run' ? '检查中…' : '立即检查开池'}
          </button>
        </div>
      </section>

      <section className="volume-spike-panel volume-spike-cloud">
        <div className="volume-spike-panel-head">
          <div>
            <h2>云端 24h 监控（企业微信）· 正式通道</h2>
            <p>
              Stage B：各底池开放后，按各自 quoteMint 监控新发 meme。Webhook 复用「持仓查询 → 机器人配置」。
            </p>
          </div>
          <span className={`volume-spike-badge tone-${cloudStatus?.enabled && cloudStatus?.hasWeCom ? 'ok' : 'warm'}`}>
            {cloudLoading ? '读取中' : (cloudStatus?.enabled ? '云端已开启' : '云端未开启')}
          </span>
        </div>

        <div className="volume-spike-cloud-grid">
          <div>
            <span>企业微信</span>
            <strong>{cloudStatus?.hasWeCom ? '已配置' : '未配置'}</strong>
            <small>{cloudStatus?.hasWeCom ? '将复用站点机器人 Webhook' : '请先到持仓查询页同步 Webhook'}</small>
          </div>
          <div>
            <span>当前阶段</span>
            <strong>{stageLabel(cloudStatus?.stage)}</strong>
            <small>
              {cloudStatus?.quoteSymbol
                ? `底池 ${cloudStatus.quoteSymbol}${cloudStatus.quoteMint ? ` · ${String(cloudStatus.quoteMint).slice(0, 8)}…` : ''}`
                : '尚未发现 LLY 底池 mint'}
            </small>
          </div>
          <div>
            <span>冷启动 / 已见代币</span>
            <strong>{cloudStatus?.seededTokens ? '已完成' : '待首次建基线'}</strong>
            <small>已记录 {cloudStatus?.seenCount ?? 0} 个 mint（同 CA 不重复报）</small>
          </div>
          <div>
            <span>最近云端检查</span>
            <strong>{cloudStatus?.lastCheckAt ? formatTime(cloudStatus.lastCheckAt) : '-'}</strong>
            <small>最近报警 {cloudStatus?.lastAlertAt ? formatTime(cloudStatus.lastAlertAt) : '暂无'}</small>
          </div>
        </div>

        <div className="volume-spike-cloud-actions">
          <button
            type="button"
            disabled={!!cloudBusy}
            onClick={() => saveCloudEnabled(!(cloudStatus?.enabled))}
          >
            {cloudBusy === 'save' ? '保存中…' : (cloudStatus?.enabled ? '关闭正式监控' : '开启正式监控')}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('run')}>
            {cloudBusy === 'run' ? '检查中…' : '立即检查一次'}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('dry')}>
            {cloudBusy === 'dry' ? '干跑中…' : '干跑（不推送）'}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('test')}>
            {cloudBusy === 'test' ? '发送中…' : '发送企微测试文案'}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => saveSettingsToCloud()}>
            {cloudBusy === 'settings' ? '保存中…' : settingsDirty ? '立即保存配置' : '配置已同步'}
          </button>
        </div>

        {cloudMessage && <div className="primary-arb-message">{cloudMessage}</div>}
        {cloudStatus?.lastError && (
          <div className="primary-arb-message is-error">最近错误：{cloudStatus.lastError}</div>
        )}
      </section>

      <section className="cxmt-config volume-spike-config volume-spike-config-6">
        <label>
          <span>24h 成交量下限（USD，0=不过滤）</span>
          <input
            type="number"
            min="0"
            step="1000"
            value={minVolume24hUsd}
            onChange={(e) => setMinVolume24hUsd(Math.max(0, Number(e.target.value) || 0))}
          />
          <small>Stage B 新开代币若 24h 量低于该值则忽略</small>
        </label>
        <div>
          <span>监控目标</span>
          <strong>LLY · Unitree · MRNA</strong>
          <small>{(cloudStatus?.targetSymbols || ['LLY', 'UNITREE', 'MRNA']).join(' / ')}</small>
        </div>
        <div>
          <span>Cron</span>
          <strong>{cloudStatus?.cron || '*/1 * * * *'}</strong>
          <small>与站点其它分钟任务共用</small>
        </div>
        <div>
          <span>底池开放通知</span>
          <strong>{cloudStatus?.pairOpenAlerted ? '已发送过' : '待触发'}</strong>
          <small>同一次开放只推一次</small>
        </div>
        <div>
          <span>阶段状态</span>
          <strong>
            <em className={`volume-spike-badge tone-${stageOk ? 'ok' : 'warm'}`}>
              {stageLabel(cloudStatus?.stage)}
            </em>
          </strong>
          <small>Stage A → Stage B 自动衔接</small>
        </div>
      </section>

      <section className="volume-spike-grid">
        <div className="volume-spike-panel">
          <div className="volume-spike-panel-head">
            <div>
              <h2>最近云端事件</h2>
              <p>企业微信已推送或冷启动记录的摘要（最多约 30 条）。</p>
            </div>
          </div>
          <div className="volume-spike-alerts">
            {events.length === 0 ? (
              <div className="volume-spike-empty">暂无事件。可先点「立刻用最新一条验证并推企微」，或开启任意底池测试等真实新发。</div>
            ) : (
              events.map((ev) => (
                <article key={ev.id || `${ev.type}-${ev.at}-${ev.mint || ''}`} className="volume-spike-alert is-multiple">
                  <header>
                    <strong>{ev.message || eventTypeLabel(ev.type)}</strong>
                    <em className={`volume-spike-badge tone-${ev.type === 'new_token' || ev.type === 'any_pool_new' ? 'bad' : 'warm'}`}>
                      {eventTypeLabel(ev.type)}
                    </em>
                  </header>
                  <p>
                    {ev.symbol
                      ? `${ev.symbol}${ev.name ? `（${ev.name}）` : ''}${ev.quoteSymbol ? ` · 底池 ${ev.quoteSymbol}` : ''}`
                      : (ev.quoteMint ? `底池 mint：${ev.quoteMint}` : '')}
                  </p>
                  <ul>
                    {ev.priceUsd != null && <li>价格 {formatUsd(ev.priceUsd)}</li>}
                    {ev.marketCapUsd != null && <li>市值 {formatUsd(ev.marketCapUsd)}</li>}
                    {ev.volume24hUsd != null && <li>24h量 {formatUsd(ev.volume24hUsd)}</li>}
                    {ev.mint && <li>CA {ev.mint}</li>}
                    <li>{ev.at ? formatTime(ev.at) : '-'}</li>
                  </ul>
                </article>
              ))
            )}
          </div>
        </div>

        <div className="volume-spike-panel">
          <div className="volume-spike-panel-head">
            <div>
              <h2>监控说明</h2>
              <p>三底池开池 + 各自 Stage B + 可选任意底池测试。</p>
            </div>
          </div>
          <div className="volume-spike-table-wrap">
            <table className="volume-spike-table">
              <thead>
                <tr>
                  <th>通道</th>
                  <th>做什么</th>
                  <th>通知内容</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Stage A 开池</td>
                  <td>扫 pairs + xstock，匹配 LLY / Unitree / MRNA</td>
                  <td>官方底池出现即推（可无人发 meme）</td>
                </tr>
                <tr>
                  <td>Stage B</td>
                  <td>按各底池 quoteMint 拉 tokens，筛新 mint</td>
                  <td>价格、市值、24h量、CA、池子</td>
                </tr>
                <tr>
                  <td>任意底池测试</td>
                  <td>tokens?sort=newest，不限底池</td>
                  <td>验证链路；验证后请手动关闭</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

